import { useState } from 'react';
import { CalendarDaysIcon, MapIcon, PhoneIcon } from '@heroicons/react/24/outline';

export type VisitTab =
  | 'needs_scheduling' | 'upcoming' | 'today' | 'completed' | 'follow_up';

export interface VisitListItem {
  id: string;
  personId?: string;
  addressLine: string;
  city: string;
  recordCount: number;
  recordSummary: string;
  personNames?: string[];
  teamName: string | null;
  statusLabel: string;
  badgeDate: string | null;
}

export interface VisitsData {
  counts: Record<VisitTab, number>;
  contextLine: string;
  items: VisitListItem[];
  total: number;
  hasMore: boolean;
}

const TABS: Array<{ id: VisitTab; label: string }> = [
  { id: 'needs_scheduling', label: 'Needs scheduling' },
  { id: 'upcoming', label: 'Upcoming' },
  { id: 'today', label: 'Today' },
  { id: 'completed', label: 'Completed' },
  { id: 'follow_up', label: 'Follow-up' },
];

const EMPTY_COPY: Record<VisitTab, string> = {
  needs_scheduling: 'Visits that still need a date or route will appear here.',
  upcoming: 'Future scheduled visits will appear here.',
  today: "Today's authorized visits will appear here.",
  completed: 'Recently completed visits will appear here.',
  follow_up: 'Outstanding calls and follow-up work will appear here.',
};

export function visitDateLabels(dateKey: string): { short: string; full: string } {
  const value = new Date(`${dateKey}T12:00:00Z`);
  return {
    short: new Intl.DateTimeFormat(undefined, {
      month: 'short', day: 'numeric', timeZone: 'UTC',
    }).format(value),
    full: new Intl.DateTimeFormat(undefined, {
      weekday: 'long', month: 'long', day: 'numeric', year: 'numeric', timeZone: 'UTC',
    }).format(value),
  };
}

export function VisitsScreen({
  data, tab, loading, loadingMore = false, error = null, canCreateTrip = false,
  canCall = false, canSchedule = false, onTabChange, onNewTrip, onSelectStop,
  onCall, onSchedule, onRetry, onLoadMore,
}: {
  data: VisitsData | null;
  tab: VisitTab;
  loading: boolean;
  loadingMore?: boolean;
  error?: { message: string; recoverable: boolean } | null;
  canCreateTrip?: boolean;
  canCall?: boolean;
  canSchedule?: boolean;
  onTabChange: (t: VisitTab) => void;
  onNewTrip: () => void;
  onSelectStop: (personId: string) => void;
  onCall?: (personId: string) => Promise<void>;
  onSchedule?: (personId: string) => Promise<void>;
  onRetry?: () => void;
  onLoadMore?: () => void;
}) {
  const items = data?.items ?? [];
  const [callingPersonId, setCallingPersonId] = useState<string | null>(null);
  const [schedulingPersonId, setSchedulingPersonId] = useState<string | null>(null);

  async function openCall(personId: string) {
    if (!onCall || callingPersonId) return;
    setCallingPersonId(personId);
    try { await onCall(personId); }
    finally { setCallingPersonId(null); }
  }

  async function openSchedule(personId: string) {
    if (!onSchedule || schedulingPersonId) return;
    setSchedulingPersonId(personId);
    try { await onSchedule(personId); }
    finally { setSchedulingPersonId(null); }
  }

  return (
    <section className="w-full min-w-0 max-w-full overflow-x-hidden pt-[18px] font-body text-[12px] text-text desktop:overflow-x-visible">
      <header className="flex min-h-[44px] w-full min-w-0 items-start justify-between gap-2">
        <h1 className="m-0 text-[28px] leading-[34px] desktop:text-[30px]">Visits</h1>
        {canCreateTrip && (
          <button
            className="inline-flex min-h-[44px] shrink-0 items-center justify-center gap-1.5 rounded bg-accent px-3 py-2 font-heading text-[11px] font-semibold text-white hover:bg-accent-600 desktop:min-h-[34px] desktop:px-3.5 desktop:py-1.5 desktop:text-[12px]"
            onClick={onNewTrip}
          >
            <MapIcon className="h-4 w-4" aria-hidden />
            New Trip
          </button>
        )}
      </header>

      <div
        role="tablist"
        aria-label="Visit status"
        className="mt-3 grid w-full max-w-full grid-cols-2 gap-x-4 pb-1 desktop:mt-[18px] desktop:flex desktop:flex-nowrap desktop:gap-x-5 desktop:overflow-x-auto desktop:scrollbar-none"
      >
        {TABS.map((item) => {
          const active = tab === item.id;
          const count = data?.counts?.[item.id];
          return (
            <button
              key={item.id}
              role="tab"
              aria-selected={active}
              onClick={() => { if (!active) onTabChange(item.id); }}
              className={`relative min-h-[44px] shrink-0 border-0 bg-transparent px-0 text-left font-heading text-[11px] transition-colors desktop:min-h-[28px] desktop:text-[12px] ${active ? 'text-accent-700 after:absolute after:inset-x-0 after:bottom-0 after:h-px after:bg-accent' : 'text-neutral-700 hover:text-accent-700'}`}
            >
              {item.label}{count !== undefined && <span className="ml-1 tabular-nums">{count}</span>}
            </button>
          );
        })}
      </div>

      {data && <p className="mt-[18px] text-[11px] text-neutral-700 desktop:mt-[22px]">{data.contextLine}</p>}

      {error ? (
        <VisitsError error={error} onRetry={onRetry} />
      ) : loading && items.length === 0 ? (
        <VisitsLoading />
      ) : items.length === 0 ? (
        <div className="mt-[22px] max-w-[520px] py-2 text-[12px] text-neutral-700">
          <p>{EMPTY_COPY[tab]}</p>
          {canCreateTrip && tab === 'needs_scheduling' && (
            <button className="mt-2 min-h-[44px] text-accent-700 underline-offset-2 hover:underline" onClick={onNewTrip}>
              Create a trip
            </button>
          )}
        </div>
      ) : (
        <>
          <ul className="mt-[18px] min-w-0" aria-busy={loadingMore}>
            {items.map((item) => <VisitRow key={item.id} item={item}
              onSelect={onSelectStop}
              onCall={canCall && onCall ? openCall : undefined}
              onSchedule={canSchedule && onSchedule ? openSchedule : undefined}
              calling={callingPersonId === item.personId}
              scheduling={schedulingPersonId === item.personId} />)}
          </ul>

          {data && data.hasMore && onLoadMore && (
            <div className="mt-3 flex items-center gap-3 text-[11px] text-neutral-600">
              <span>Showing {items.length} of {data.total} locations</span>
              <button
                className="min-h-[44px] px-2 font-semibold text-accent-700 hover:underline disabled:opacity-50 desktop:min-h-[34px]"
                disabled={loadingMore}
                onClick={onLoadMore}
              >
                {loadingMore ? 'Loading…' : 'Load more'}
              </button>
            </div>
          )}
        </>
      )}
    </section>
  );
}

function VisitRow({ item, onSelect, onCall, onSchedule, calling, scheduling }: {
  item: VisitListItem;
  onSelect: (personId: string) => void;
  onCall?: (personId: string) => Promise<void>;
  onSchedule?: (personId: string) => Promise<void>;
  calling: boolean;
  scheduling: boolean;
}) {
  const personNames = item.personNames?.length
    ? item.personNames.join(' · ')
    : item.recordSummary;
  const details = [
    item.statusLabel,
    item.city,
    item.recordCount > 1 ? `${item.recordCount} records` : '',
  ].filter(Boolean);
  const badge = item.badgeDate ? visitDateLabels(item.badgeDate) : null;
  const disabled = !item.personId;

  return (
    <li className="flex min-w-0 items-stretch gap-2">
      <button
        type="button"
        disabled={disabled}
        onClick={() => item.personId && onSelect(item.personId)}
        className="group relative block min-h-[68px] min-w-0 flex-1 overflow-hidden rounded py-2 pl-0 pr-16 text-left transition-colors hover:bg-neutral-200 focus-visible:bg-neutral-200 disabled:cursor-default desktop:min-h-[62px] desktop:py-[9px]"
      >
        <span className="min-w-0 overflow-hidden pr-1">
          <span className="block break-words font-heading text-[13px] font-semibold leading-[17px] text-text desktop:text-[13px] desktop:leading-[18px]">
            {personNames}
          </span>
          <span className="mt-0.5 block truncate text-[12px] font-medium leading-[16px] text-text">
            {item.addressLine}
          </span>
          <span className="mt-0.5 block truncate text-[10px] leading-[14px] text-neutral-700">
            {details.join(' · ')}
          </span>
        </span>
        {badge && (
          <time
            dateTime={item.badgeDate ?? undefined}
            title={badge.full}
            aria-label={badge.full}
            className="absolute right-0 top-2 w-12 rounded bg-alert-100 px-1 py-1 text-center font-heading text-[10px] leading-[14px] text-alert-800 desktop:top-[9px] desktop:w-auto desktop:px-2"
          >
            {badge.short}
          </time>
        )}
      </button>
      {onCall && item.personId && (
        <button
          type="button"
          disabled={calling}
          aria-label={`Call ${item.personNames?.[0] ?? item.recordSummary}`}
          className="my-2 inline-flex min-h-[44px] w-[68px] shrink-0 items-center justify-center gap-1 rounded border border-accent-500 px-2 font-heading text-[11px] font-semibold text-accent-700 hover:bg-accent-100 focus-visible:bg-accent-100 disabled:opacity-60 desktop:my-[9px]"
          onClick={() => void onCall(item.personId!)}
        >
          <PhoneIcon className="h-4 w-4" aria-hidden />
          {calling ? 'Opening' : 'Call'}
        </button>
      )}
      {onSchedule && item.personId && (
        <button
          type="button"
          disabled={scheduling}
          aria-label={`Schedule ${item.personNames?.[0] ?? item.recordSummary}`}
          className="my-2 inline-flex min-h-[44px] w-[82px] shrink-0 items-center justify-center gap-1 rounded border border-accent-500 px-2 font-heading text-[11px] font-semibold text-accent-700 hover:bg-accent-100 focus-visible:bg-accent-100 disabled:opacity-60 desktop:my-[9px]"
          onClick={() => void onSchedule(item.personId!)}
        >
          <CalendarDaysIcon className="h-4 w-4" aria-hidden />
          {scheduling ? 'Opening' : 'Schedule'}
        </button>
      )}
    </li>
  );
}

function VisitsLoading() {
  return (
    <div className="mt-[18px] min-h-[260px]" role="status" aria-label="Loading visits">
      <span className="sr-only">Loading visits…</span>
      {Array.from({ length: 5 }, (_, index) => (
        <div key={index} aria-hidden className="flex min-h-[50px] items-start gap-4 py-2">
          <span className="flex-1">
            <span className="block h-3 w-[min(430px,72vw)] rounded bg-neutral-200" />
            <span className="mt-2 block h-2 w-[min(330px,58vw)] rounded bg-neutral-200" />
          </span>
          <span className="h-6 w-12 rounded bg-alert-100" />
        </div>
      ))}
    </div>
  );
}

function VisitsError({ error, onRetry }: {
  error: { message: string; recoverable: boolean };
  onRetry?: () => void;
}) {
  return (
    <div role="alert" className="mt-[22px] max-w-[520px] py-2 text-[12px]">
      <p className="font-semibold">Visits could not be loaded.</p>
      <p className="mt-1 text-neutral-700">{error.message}</p>
      {error.recoverable && onRetry && (
        <button className="mt-2 min-h-[44px] font-semibold text-accent-700 hover:underline" onClick={onRetry}>
          Retry
        </button>
      )}
    </div>
  );
}
